import { ARCHETYPE_BY_ID, ARCHETYPE_FAMILIES } from "@nano/content";
import { useState } from "react";
import { closeOverlay, pickAvatar, pickRandomAvatar, restoreJourney } from "../../app/actions.ts";
import { useApp } from "../../app/store.ts";
import { archetypeName, familyName } from "../../i18n/content.ts";
import { vi } from "../../i18n/vi.ts";
import { AvatarPreview } from "../components/AvatarPreview.tsx";
import { Art, Button, LanguageSwitch, Sheet } from "../components/common.tsx";

export function Splash() {
  const progress = useApp((s) => s.ui.bootProgress);
  return (
    <div className="splash" data-testid="splash">
      <Art id="logo.main" className="splash__logo" alt="NANO Magical City" />
      <div className="splash__bar" role="progressbar" aria-valuenow={Math.round(progress * 100)}>
        <span style={{ width: `${Math.max(6, progress * 100)}%` }} />
      </div>
      <p className="splash__text">{vi.loading}</p>
      <p className="credit">{vi.credit}</p>
    </div>
  );
}

/** One-tap character choice. No forms, no names, no login. */
export function AvatarPick({ mode }: { mode: "first" | "change" }) {
  const [restoring, setRestoring] = useState(false);
  const current = useApp((s) => s.save?.profile.avatarId ?? null);
  const content = (
    <>
      <div className="pick__families">
        {ARCHETYPE_FAMILIES.map((family) => (
          <section
            key={family.id}
            className="pick__family"
            style={{ gridColumn: `span ${family.members.length}` }}
            data-testid={`pick-family-${family.id}`}
          >
            <h2 className="pick__family-name">{familyName(family)}</h2>
            <div className="pick__cards" style={{ gridTemplateColumns: `repeat(${family.members.length}, minmax(0, 1fr))` }}>
              {family.members.map((id) => {
                const archetype = ARCHETYPE_BY_ID.get(id);
                if (!archetype) return null;
                return (
                  <button
                    key={archetype.id}
                    type="button"
                    className={`pick__card ${current === archetype.id ? "pick__card--current" : ""}`}
                    onClick={() => void pickAvatar(archetype.id)}
                    data-testid={`pick-${archetype.id}`}
                  >
                    <AvatarPreview avatarId={archetype.id} equipped={{ head: archetype.starterItemId }} height={112} sidePad={0.04} bounce />
                    <span className="pick__name">{archetypeName(archetype)}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      {mode === "first" ? (
        <div className="pick__actions">
          <Button variant="secondary" onClick={() => void pickRandomAvatar()} testId="pick-random">
            {vi.pick.random}
          </Button>
          <button type="button" className="link" onClick={() => setRestoring(true)}>
            {vi.pick.restore}
          </button>
        </div>
      ) : null}
      {restoring ? <RestoreDialog onClose={() => setRestoring(false)} /> : null}
    </>
  );

  if (mode === "change") {
    return (
      <Sheet title={vi.settings.character} onClose={() => closeOverlay()}>
        {content}
      </Sheet>
    );
  }
  return (
    <div className="pick" data-testid="avatar-pick">
      <LanguageSwitch className="pick__lang" />
      <Art id="logo.main" className="pick__logo" alt="NANO Magical City" />
      <h1 className="pick__title">{vi.pick.title}</h1>
      <p className="pick__subtitle">{vi.pick.subtitle}</p>
      {content}
      <p className="credit">{vi.credit}</p>
    </div>
  );
}

export function RestoreDialog({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const online = useApp((s) => s.ui.online);
  const submit = async () => {
    setBusy(true);
    const ok = await restoreJourney(code);
    setBusy(false);
    if (ok) onClose();
    else setConfirming(false);
  };
  return (
    <Sheet title={vi.settings.restore} onClose={onClose} testId="restore-dialog">
      <p>{vi.settings.restoreBody}</p>
      <input
        className="code-input"
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        placeholder={vi.settings.restorePlaceholder}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        maxLength={32}
        data-testid="restore-input"
      />
      {!online ? <p className="note">{vi.shop.offline.split(".")[0]}.</p> : null}
      {confirming ? (
        <div className="confirm">
          <p>{vi.settings.restoreConfirm}</p>
          <Button variant="danger" onClick={() => void submit()} disabled={busy} testId="restore-confirm">
            {vi.settings.restoreYes}
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            {vi.cancel}
          </Button>
        </div>
      ) : (
        <Button onClick={() => setConfirming(true)} disabled={code.replace(/[^0-9A-Z]/g, "").length < 20 || !online} testId="restore-submit">
          {vi.settings.restore}
        </Button>
      )}
      <p className="note">{vi.settings.honest}</p>
    </Sheet>
  );
}
